import { useReducer, useEffect, useRef } from 'react';
import type { AppState, AppAction } from '@/lib/types';
import { makeInitialState, reducerWithTimestamp } from '@/lib/reducer';
import { saveLocal, loadLocal, runLegacyMigration } from '@/lib/storage';

export function useAppState() {
  const migrated = useRef(false);
  if (!migrated.current) {
    runLegacyMigration();
    migrated.current = true;
  }

  const [state, dispatch] = useReducer(
    reducerWithTimestamp,
    null,
    () => {
      const saved = loadLocal();
      if (saved) {
        const { trackingSession, ...rest } = saved;
        return { ...makeInitialState(), ...rest, trackingSession };
      }
      return makeInitialState();
    }
  );

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    saveLocal(state);
  }, [state]);

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === 'liftforge_state' && e.newValue) {
        try {
          const data = JSON.parse(e.newValue);
          dispatch({ type: 'LOAD_FROM_SHEETS', data });
        } catch {}
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  return { state, dispatch };
}
